import type { VehicleType } from '../../store/transit'
import { metresBetween, offsetPoint } from './trainConsist'
import type {
  GeometryLod,
  ProjectedViewport,
  TrainMapPosition,
  TrainPointProjector,
  TrainRenderProfile,
} from './trainRenderLod'

export interface VehicleDatum {
  vehicleId: string
  position: [number, number]
  color: [number, number, number, number]
  routeId: string
  bearing: number
  type: VehicleType
  segIdx: number      // 0 = front car, trainCars-1 = rear (always 0 in single-car mode and for buses)
  boarded: boolean    // true for the vehicle the rider has boarded (distinct highlight tint)
  watched: boolean    // true for the vehicle held by the independent third-person camera
  destinationName?: string
  carLabel?: string
  /** Resolved once for the whole subway consist before cars or decals are expanded. */
  trainRenderProfile?: TrainRenderProfile
  /** Decal-selector aliases; populated only for subway datums. */
  carIndex?: number
  destination?: string
  carNumber?: string
}

/** New projected-size policy is on by default; explicit `false` restores the legacy ladder. */
export const DISTANCE_TRAIN_LOD_ENABLED = import.meta.env.VITE_TRAIN_DISTANCE_LOD !== 'false'

export type VehicleLayerDisplayMode = 'labels' | 'trains' | 'both' | 'optimized'

/**
 * The WATCHED vehicle overrides labels mode and must continue through the mesh/decal path:
 * the chase camera is a close-up OF the vehicle, and a route bullet filling the screen is
 * not a thing anyone asked to look at. It is also explicit and temporary — the rider turned
 * it on and can turn it off.
 *
 * ⚠️ **Boarding deliberately does NOT override it**, and used to. A ride lasts the whole
 * trip, so pinning the boarded vehicle to a mesh meant a rider who had chosen `labels` got
 * a 3D train for as long as they were aboard, with no setting that would turn it off. That
 * is the same fact reported as "the 3D toggle doesn't switch off": watching and boarding
 * both forced a mesh, so releasing the toggle changed nothing the rider could see. Which
 * vehicle you are on is carried by the rider ring and the highlighted `boarded-route` line,
 * not by geometry — NOT by a tint, which was removed from both fleets (a flat per-instance
 * colour repaints the model instead of marking it). The ring is a DOM marker, so unlike a
 * mesh it survives every display mode; it covers buses as well as trains for exactly this
 * reason. `trainRenderLodPolicy` still pins a boarded vehicle to L0 within the MESH modes,
 * so your own ride is never the one demoted by distance.
 */
export function shouldUseLabelOnlyVehicleLayers(
  displayMode: VehicleLayerDisplayMode,
  watchedVehicleId: string | null,
): boolean {
  return displayMode === 'labels' && watchedVehicleId === null
}

export interface TrainLodCameraState {
  longitude: number
  latitude: number
  zoom: number
  pitch: number
  bearing: number
  width: number
  height: number
}

export interface TrainLodClassificationInput {
  vehicleId: string
  anchor: TrainMapPosition
  heading: number
  watched: boolean
  boarded: boolean
  displayMode: string
  camera: TrainLodCameraState
}

export const TRAIN_LOD_CACHE_TOLERANCE = Object.freeze({
  cameraCenterMetres: 2,
  trainMovementMetres: 1,
  zoom: 0.01,
  pitchDegrees: 0.25,
  bearingDegrees: 0.5,
  trainHeadingDegrees: 1,
  viewportPixels: 1,
})

function angleDistance(a: number, b: number): number {
  return Math.abs(((a - b + 540) % 360) - 180)
}

function canReuseTrainLod(
  previous: TrainLodClassificationInput,
  next: TrainLodClassificationInput,
): boolean {
  const tolerance = TRAIN_LOD_CACHE_TOLERANCE
  return previous.vehicleId === next.vehicleId
    && previous.watched === next.watched
    && previous.boarded === next.boarded
    && previous.displayMode === next.displayMode
    && metresBetween(
      { lon: previous.anchor[0], lat: previous.anchor[1] },
      { lon: next.anchor[0], lat: next.anchor[1] },
    ) <= tolerance.trainMovementMetres
    && angleDistance(previous.heading, next.heading) <= tolerance.trainHeadingDegrees
    && metresBetween(
      { lon: previous.camera.longitude, lat: previous.camera.latitude },
      { lon: next.camera.longitude, lat: next.camera.latitude },
    ) <= tolerance.cameraCenterMetres
    && Math.abs(previous.camera.zoom - next.camera.zoom) <= tolerance.zoom
    && Math.abs(previous.camera.pitch - next.camera.pitch) <= tolerance.pitchDegrees
    && angleDistance(previous.camera.bearing, next.camera.bearing) <= tolerance.bearingDegrees
    && Math.abs(previous.camera.width - next.camera.width) <= tolerance.viewportPixels
    && Math.abs(previous.camera.height - next.camera.height) <= tolerance.viewportPixels
}

/** Cache around the stateful hysteresis policy; classification runs only on meaningful movement. */
export class TrainLodClassificationCache<Value> {
  readonly #entries = new Map<string, { input: TrainLodClassificationInput; value: Value }>()

  resolve(
    input: TrainLodClassificationInput,
    classify: () => Value,
  ): { value: Value; cacheHit: boolean } {
    const previous = this.#entries.get(input.vehicleId)
    if (previous && canReuseTrainLod(previous.input, input)) {
      return { value: previous.value, cacheHit: true }
    }
    const value = classify()
    this.#entries.set(input.vehicleId, {
      input: {
        ...input,
        anchor: [input.anchor[0], input.anchor[1]],
        camera: { ...input.camera },
      },
      value,
    })
    return { value, cacheHit: false }
  }

  cleanup(activeVisibleVehicleIds: Iterable<string>): number {
    const active = new Set(activeVisibleVehicleIds)
    let removed = 0
    for (const vehicleId of this.#entries.keys()) {
      if (active.has(vehicleId)) continue
      this.#entries.delete(vehicleId)
      removed++
    }
    return removed
  }

  clear(): void {
    this.#entries.clear()
  }
}

export interface ProjectedConsistCullInput {
  anchor: TrainMapPosition
  heading: number
  project: TrainPointProjector
  viewport: ProjectedViewport
  consistLengthMetres: number
  /** Keeps the route marker/cab nose from clipping exactly at the viewport edge. */
  minimumMarginPixels?: number
}

/**
 * Project a consist length at its actual latitude/bearing and use that screen-space size
 * as cull padding. This replaces the old fixed 0.05-degree (multi-kilometre) expansion.
 */
export function projectedConsistIntersectsViewport(input: ProjectedConsistCullInput): boolean {
  try {
    const anchor = input.project(input.anchor)
    const tailPoint = offsetPoint(
      { lon: input.anchor[0], lat: input.anchor[1] },
      input.heading + 180,
      Math.max(0, input.consistLengthMetres),
    )
    const tail = input.project([tailPoint.lon, tailPoint.lat])
    if (!anchor || !tail || !Number.isFinite(anchor.x) || !Number.isFinite(anchor.y)
      || !Number.isFinite(tail.x) || !Number.isFinite(tail.y)) return false
    const margin = Math.max(
      input.minimumMarginPixels ?? 16,
      Math.hypot(tail.x - anchor.x, tail.y - anchor.y),
    )
    return anchor.x >= -margin
      && anchor.x <= input.viewport.width + margin
      && anchor.y >= -margin
      && anchor.y <= input.viewport.height + margin
  } catch {
    return false
  }
}

/** Shared cull decision for animation frames and synchronous filter/route rebuilds. */
export function shouldCullProjectedVehicle(
  input: ProjectedConsistCullInput & { pinned: boolean },
): boolean {
  return !input.pinned && !projectedConsistIntersectsViewport(input)
}

export type TrainMeshLod = Exclude<GeometryLod, 'marker'>
export type TrainBody = 'A' | 'B'
export type TrainLodBodyBucketKey = `${TrainMeshLod}:${TrainBody}`

/** One pass into only the model buckets that contain instances; marker datums are ignored. */
export function bucketTrainCarsByLod<T extends { vehicleId: string }>(
  cars: readonly T[],
  geometryFor: (car: T) => GeometryLod,
  bodyFor: (car: T) => TrainBody,
): Map<TrainLodBodyBucketKey, T[]> {
  const buckets = new Map<TrainLodBodyBucketKey, T[]>()
  const tierByTrain = new Map<string, GeometryLod>()
  for (const car of cars) {
    const tier = geometryFor(car)
    const previousTier = tierByTrain.get(car.vehicleId)
    if (previousTier && previousTier !== tier) {
      throw new Error(`Train ${car.vehicleId} split across geometry tiers ${previousTier} and ${tier}`)
    }
    tierByTrain.set(car.vehicleId, tier)
    if (tier === 'marker') continue
    const key: TrainLodBodyBucketKey = `${tier}:${bodyFor(car)}`
    const bucket = buckets.get(key)
    if (bucket) bucket.push(car)
    else buckets.set(key, [car])
  }
  return buckets
}

